// src/pages/AdminCampaignKPIs.jsx
// Dashboard des 8 KPIs de la campagne « The Collection After You ».
//
// La donnée vient de la fonction SQL campaign_funnel_kpis() (agrégats first-party,
// funnel_events + auth.users + profiles), servie par l'edge function `campaign-kpis`
// qui exige profiles.role='admin'. Le front n'appelle JAMAIS la RPC directement :
// elle est réservée à service_role. Ici, functions.invoke attache le JWT admin,
// et l'edge function refait la vérification côté serveur (défense en profondeur).
import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../lib/supabaseClient";

const PERIODS = [
  { days: 7, label: "7 jours" },
  { days: 30, label: "30 jours" },
  { days: 90, label: "90 jours" },
];

// Taux en % (numérateur / dénominateur), robuste au dénominateur nul.
function rate(num, den) {
  if (!den || den <= 0) return null;
  return Math.round((num / den) * 1000) / 10; // 1 décimale
}

function RateBadge({ value }) {
  if (value === null) {
    return <span className="text-xs text-gray-400">—</span>;
  }
  const tone =
    value >= 50 ? "bg-green-100 text-green-700"
    : value >= 20 ? "bg-amber-100 text-amber-700"
    : "bg-rose-100 text-rose-700";
  return (
    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${tone}`}>
      {value}%
    </span>
  );
}

// Une marche de l'entonnoir : nom, effectif, conversion depuis l'étape précédente,
// et une barre dont la largeur est proportionnelle à l'effectif du haut de tunnel.
function FunnelStep({ label, count, prevCount, topCount, hint }) {
  const conv = prevCount === undefined ? null : rate(count, prevCount);
  const width = topCount && topCount > 0 ? Math.max(2, (count / topCount) * 100) : 0;
  return (
    <div className="py-3">
      <div className="flex items-center justify-between mb-1.5">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-gray-800">{label}</span>
          {hint && <span className="text-xs text-gray-400">{hint}</span>}
        </div>
        <div className="flex items-center gap-3">
          <span className="text-lg font-bold text-gray-900 tabular-nums">{count ?? 0}</span>
          {prevCount !== undefined && <RateBadge value={conv} />}
        </div>
      </div>
      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
        <div
          className="h-full bg-gradient-to-r from-amber-400 to-amber-500 rounded-full transition-all duration-500"
          style={{ width: `${width}%` }}
        />
      </div>
    </div>
  );
}

export default function AdminCampaignKPIs() {
  const [days, setDays] = useState(30);
  const [kpis, setKpis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async (period) => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: fnErr } = await supabase.functions.invoke("campaign-kpis", {
        body: { days: period },
      });
      if (fnErr) {
        // 403 → non-admin ; on message sans fuite.
        const status = fnErr?.context?.status;
        if (status === 403) throw new Error("Accès réservé aux administrateurs.");
        if (status === 401) throw new Error("Session expirée — reconnectez-vous.");
        throw new Error(fnErr.message || "Erreur de chargement.");
      }
      setKpis(data?.kpis ?? null);
    } catch (e) {
      setError(e.message || String(e));
      setKpis(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(days); }, [days, load]);

  // Décomposition des 8 KPIs.
  const k = kpis || {};
  const k1 = k.k1_visite_vers_quiz || {};
  const k2 = k.k2_quiz_demarre_termine || {};
  const k3 = k.k3_termine_email || {};
  const k4 = k.k4_email_compte_active || {};
  const k5 = k.k5_compte_premiere_oeuvre || {};
  const k6 = k.k6_oeuvre_document || {};
  const k8 = k.k8_essai_payant || {};
  const life = k.lifecycle || {};
  const invitations = k.k7_invitations ?? 0;

  const visites = k1.visites ?? 0;
  const emails = k3.emails ?? 0;
  // K-facteur viral grossier : invitations envoyées par email capté.
  const kFactor = emails > 0 ? Math.round((invitations / emails) * 100) / 100 : null;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-6 py-6 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-serif font-bold text-gray-900">
              Campagne — The Collection After You
            </h1>
            <p className="text-sm text-gray-500 mt-1">Entonnoir d'acquisition, KPIs first-party</p>
          </div>
          <Link to="/admin" className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg">
            ← Admin
          </Link>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-8">
        {/* Sélecteur de période */}
        <div className="flex items-center gap-2 mb-6">
          {PERIODS.map((p) => (
            <button
              key={p.days}
              onClick={() => setDays(p.days)}
              className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                days === p.days ? "bg-gray-900 text-white" : "bg-white text-gray-700 border border-gray-200 hover:bg-gray-100"
              }`}
            >
              {p.label}
            </button>
          ))}
          {loading && <span className="text-sm text-gray-400 ml-2">Chargement…</span>}
        </div>

        {error && (
          <div className="bg-rose-50 border border-rose-200 text-rose-700 rounded-xl px-5 py-4 mb-6 text-sm">
            {error}
          </div>
        )}

        {!error && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Entonnoir (8 marches, colonnes 1-2) */}
            <div className="lg:col-span-2 bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500 mb-2">
                Entonnoir
              </h2>
              <div className="divide-y divide-gray-100">
                <FunnelStep label="Visites landing" count={visites} topCount={visites} />
                <FunnelStep label="Quiz démarrés" count={k1.quiz_demarres} prevCount={visites} topCount={visites} hint="K1" />
                <FunnelStep label="Quiz terminés" count={k2.termines} prevCount={k1.quiz_demarres} topCount={visites} hint="K2" />
                <FunnelStep label="Emails soumis" count={emails} prevCount={k2.termines} topCount={visites} hint="K3" />
                <FunnelStep label="Comptes activés" count={k4.comptes_actives} prevCount={emails} topCount={visites} hint="K4" />
                <FunnelStep label="Première œuvre ajoutée" count={k5.avec_oeuvre} prevCount={k5.comptes} topCount={visites} hint="K5" />
                <FunnelStep label="Premier document ajouté" count={k6.avec_document} prevCount={k6.avec_oeuvre} topCount={visites} hint="K6" />
                <FunnelStep label="Essai → payant" count={k8.payants} prevCount={k8.comptes} topCount={visites} hint="K8" />
              </div>
            </div>

            {/* Boucle virale + résumé (colonne 3) */}
            <div className="space-y-6">
              <div className="bg-gradient-to-br from-violet-50 to-violet-100 rounded-2xl border border-violet-200 p-6">
                <h2 className="text-sm font-semibold uppercase tracking-wide text-violet-700 mb-3">
                  Boucle virale (K7)
                </h2>
                <div className="text-4xl font-bold text-gray-900">{invitations}</div>
                <p className="text-sm text-gray-600 mt-1">invitations envoyées</p>
                <div className="mt-4 pt-4 border-t border-violet-200">
                  <div className="flex items-baseline gap-2">
                    <span className="text-2xl font-bold text-gray-900">
                      {kFactor === null ? "—" : kFactor}
                    </span>
                    <span className="text-xs text-gray-500">invitation / email capté</span>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 text-xs text-gray-500 leading-relaxed">
                Effectifs par <span className="font-medium text-gray-700">visiteur unique</span> (pré-compte)
                et par <span className="font-medium text-gray-700">compte issu du quiz</span> (post-compte).
                Le taux de chaque marche est calculé sur l'étape précédente.
                Données first-party (aucun tiers).
              </div>
            </div>
          </div>
        )}

        {/* Cycle de vie des essais — le moment J+14 où se joue la conversion.
            « Expirent sous 3 j » est le point d'action : ces comptes vont hit le
            paywall, c'est la fenêtre de relance. */}
        {!error && (
          <div className="mt-6 bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500 mb-4">
              Cycle de vie des essais
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="rounded-xl border border-gray-200 p-4">
                <div className="text-3xl font-bold text-gray-900">{life.essais_actifs ?? 0}</div>
                <div className="text-xs text-gray-500 mt-1">essais en cours</div>
              </div>
              <div className={`rounded-xl border p-4 ${(life.essais_expirent_3j ?? 0) > 0 ? "border-amber-300 bg-amber-50" : "border-gray-200"}`}>
                <div className={`text-3xl font-bold ${(life.essais_expirent_3j ?? 0) > 0 ? "text-amber-700" : "text-gray-900"}`}>
                  {life.essais_expirent_3j ?? 0}
                </div>
                <div className="text-xs text-gray-500 mt-1">expirent sous 3 j</div>
              </div>
              <div className={`rounded-xl border p-4 ${(life.essais_expires_non_convertis ?? 0) > 0 ? "border-rose-200 bg-rose-50" : "border-gray-200"}`}>
                <div className={`text-3xl font-bold ${(life.essais_expires_non_convertis ?? 0) > 0 ? "text-rose-700" : "text-gray-900"}`}>
                  {life.essais_expires_non_convertis ?? 0}
                </div>
                <div className="text-xs text-gray-500 mt-1">expirés non convertis</div>
              </div>
              <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4">
                <div className="text-3xl font-bold text-emerald-700">{life.convertis ?? 0}</div>
                <div className="text-xs text-gray-500 mt-1">convertis (payants)</div>
              </div>
            </div>
            <p className="text-xs text-gray-400 mt-4">
              Essai cardless de 14 j (modèle A). « Convertis » = statut d'abonnement Stripe actif —
              écrit par le webhook une fois le paiement encaissé.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
